import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const TableOfContents = ({ content, isMobileOpen = false, onMobileClose }: TableOfContentsProps) => {
  const [headings, setHeadings] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    // Parse HTML content to extract headings
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const headingElements = doc.querySelectorAll('h1, h2, h3, h4');
    
    const tocItems: TocItem[] = [];
    headingElements.forEach((heading, index) => {
      const id = `heading-${index}`;
      const text = heading.textContent || '';
      const level = parseInt(heading.tagName.substring(1));
      tocItems.push({ id, text, level });
    });
    
    setHeadings(tocItems);
  }, [content]);

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = document.querySelectorAll('.article-content h1, .article-content h2, .article-content h3, .article-content h4');
      
      let current = '';
      headingElements.forEach((heading, index) => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 150) {
          current = `heading-${index}`;
        }
      });
      
      setActiveId(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToHeading = (index: number) => {
    const element = document.querySelectorAll('.article-content h1, .article-content h2, .article-content h3, .article-content h4')[index];
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
      onMobileClose?.();
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className="hidden lg:block lg:sticky lg:top-24 w-64 max-h-[calc(100vh-6rem)] overflow-y-auto">
      <div className="glass-morphism rounded-xl p-4">
          <h3 className="text-base font-bold mb-3 gradient-text">
            Table of Contents
          </h3>
          <nav className="space-y-1">
            {headings.map((heading, index) => (
              <button
                key={index}
                onClick={() => scrollToHeading(index)}
                className={`
                  block w-full text-left py-1.5 px-2 rounded-lg text-xs sm:text-sm transition-all
                  ${activeId === heading.id 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium' 
                    : 'text-muted-foreground hover:bg-muted/50'
                  }
                  ${heading.level === 2 ? 'pl-2' : ''}
                  ${heading.level === 3 ? 'pl-4' : ''}
                  ${heading.level === 4 ? 'pl-6' : ''}
                `}
              >
                {heading.text}
              </button>
            ))}
          </nav>
        </div>
    </div>
  );
};

export default TableOfContents;
